import {
  ArrowUpward,
  BorderColorOutlined,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material'
import React from 'react'

import {
  Container,
  Footer,
  Header,
  HeaderChip,
  HeaderLeft,
  NavButton,
  NavCluster,
  NavCounter,
  OptionList,
  OptionRow,
  PencilIcon,
  QuestionPage,
  QuestionText,
  Section,
  StyledInput,
  SubmitButton,
} from 'features/ask-yogi/components/ask-user-input-widget.styles'
import { AskUserInputOptions } from 'features/ask-yogi/components/AskUserInputOptions'
import { useAskUserInputWidget } from 'features/ask-yogi/components/use-ask-user-input-widget'
import type { AskUserQuestionArtifact } from 'features/ask-yogi/types'

export type AskUserInputWidgetProps = {
  artifact: AskUserQuestionArtifact
  // Receives the readable answer text ("<header>: <value>" per answered
  // question). Absent in read-only / shared views, which renders disabled.
  onSubmit?: (content: string) => void
  disabled?: boolean
}

export const AskUserInputWidget: React.FC<AskUserInputWidgetProps> = ({
  artifact,
  onSubmit,
  disabled = false,
}) => {
  const {
    questions,
    currentIndex,
    keyboardIndex,
    direction,
    question,
    state,
    options,
    isReadOnly,
    isPaged,
    isFirstPage,
    isLastPage,
    isOtherKeyboardFocused,
    hasOtherText,
    hasAnyAnswer,
    showSubmit,
    containerRef,
    inputRef,
    goBack,
    goNext,
    activateOption,
    handleOtherChange,
    commitOther,
    submitAll,
  } = useAskUserInputWidget({ artifact, onSubmit, disabled })

  const uid = React.useId()

  if (!question) return null

  const activeRowId =
    keyboardIndex < 0
      ? undefined
      : keyboardIndex < options.length
        ? `${uid}-opt-${keyboardIndex}`
        : `${uid}-other`

  return (
    <Container ref={containerRef} tabIndex={-1} data-shortcut-scope>
      <Section>
        <Header>
          <HeaderLeft>
            <HeaderChip>{question.header}</HeaderChip>
            <QuestionText>{question.question}</QuestionText>
          </HeaderLeft>
          {isPaged && (
            <NavCluster>
              <NavButton
                type="button"
                onClick={goBack}
                disabled={isFirstPage}
                aria-label="Previous question"
              >
                <ChevronLeft fontSize="small" />
              </NavButton>
              <NavCounter>
                {currentIndex + 1} of {questions.length}
              </NavCounter>
              <NavButton
                type="button"
                onClick={goNext}
                disabled={isLastPage}
                aria-label="Next question"
              >
                <ChevronRight fontSize="small" />
              </NavButton>
            </NavCluster>
          )}
        </Header>

        <QuestionPage
          key={`${direction}-${currentIndex}`}
          $direction={direction}
        >
          <OptionList
            role="listbox"
            aria-label={question.question}
            aria-activedescendant={activeRowId}
          >
            <AskUserInputOptions
              options={options}
              selectedLabels={state.selectedLabels}
              keyboardIndex={keyboardIndex}
              isReadOnly={isReadOnly}
              rowIdPrefix={uid}
              onActivate={activateOption}
            />
            <OptionRow
              id={`${uid}-other`}
              $keyboardFocused={isOtherKeyboardFocused}
              $selected={state.otherActive}
              $readOnly={isReadOnly}
              role="option"
              aria-selected={state.otherActive}
              onClick={() => inputRef.current?.focus()}
            >
              <PencilIcon>
                <BorderColorOutlined sx={{ fontSize: 14 }} />
              </PencilIcon>
              <StyledInput
                ref={inputRef}
                type="text"
                placeholder="Something else"
                value={state.otherText}
                disabled={isReadOnly}
                onChange={(e) => handleOtherChange(e.target.value)}
                aria-label={`Other answer for ${question.header}`}
              />
              {!question.multi_select && hasOtherText && !isReadOnly && (
                <SubmitButton
                  type="button"
                  onClick={commitOther}
                  $enabled
                  aria-label="Use custom answer"
                >
                  <ArrowUpward sx={{ fontSize: 16 }} />
                </SubmitButton>
              )}
            </OptionRow>
          </OptionList>
        </QuestionPage>

        {showSubmit && (
          <Footer>
            <SubmitButton
              type="button"
              onClick={submitAll}
              disabled={isReadOnly || !hasAnyAnswer}
              $enabled={!isReadOnly && hasAnyAnswer}
              aria-label="Submit answers"
            >
              <ArrowUpward sx={{ fontSize: 16 }} />
            </SubmitButton>
          </Footer>
        )}
      </Section>
    </Container>
  )
}
